import { useEffect, useRef, type RefObject } from 'react';

/**
 * Volume-egalisatie in de browser, zoals Plezy dat met mpv doet: realtime, zonder dat Plex
 * eerst alles hoeft te analyseren. Web Audio: compressor (dynamiek afvlakken) → versterking
 * (zachte stukken omhoog) → begrenzer (geen pieken/vervorming).
 *
 * Let op: een video-element kan maar één keer aan Web Audio gekoppeld worden. Daarom blijft de
 * keten bestaan en zetten we bij "uit" alleen de instellingen neutraal.
 */

type Mode = 'off' | 'normal' | 'night';

const SETTINGS: Record<Mode, { threshold: number; knee: number; ratio: number; attack: number; release: number; gain: number }> = {
  off: { threshold: 0, knee: 0, ratio: 1, attack: 0.003, release: 0.25, gain: 1 },
  normal: { threshold: -26, knee: 24, ratio: 3.5, attack: 0.005, release: 0.3, gain: 1.6 },
  night: { threshold: -40, knee: 18, ratio: 10, attack: 0.002, release: 0.4, gain: 2.6 },
};

interface Chain {
  ctx: AudioContext;
  comp: DynamicsCompressorNode;
  gain: GainNode;
}

const chains = new WeakMap<HTMLMediaElement, Chain>();

/** Loopt het geluid van dit element al via Web Audio? Dan moet elke nieuwe bron met CORS geladen worden. */
export function hasLoudnessChain(el: HTMLMediaElement): boolean {
  return chains.has(el);
}

function chainFor(el: HTMLMediaElement): Chain | null {
  const hit = chains.get(el);
  if (hit) return hit;
  const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  try {
    const ctx = new Ctx();
    const src = ctx.createMediaElementSource(el);
    const comp = ctx.createDynamicsCompressor();
    const gain = ctx.createGain();
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -2;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.1;
    src.connect(comp).connect(gain).connect(limiter).connect(ctx.destination);
    const chain = { ctx, comp, gain };
    chains.set(el, chain);
    // Browsers starten Web Audio pas na een gebruikersactie: bij afspelen hervatten
    el.addEventListener('play', () => void ctx.resume().catch(() => undefined));
    return chain;
  } catch {
    return null;
  }
}

export function useLoudness(video: RefObject<HTMLVideoElement | null>, mode: Mode): void {
  const used = useRef(false);

  // Bij het sluiten van de player de audio-keten opruimen (browsers staan maar een paar tegelijk toe)
  useEffect(() => {
    const el = video.current;
    return () => {
      const chain = el ? chains.get(el) : undefined;
      if (chain && el) {
        chains.delete(el);
        void chain.ctx.close().catch(() => undefined);
      }
    };
  }, [video]);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    // Pas koppelen als het voor het eerst aan gaat; daarna blijft de keten bestaan
    if (mode === 'off' && !used.current) return;
    const chain = chainFor(el);
    if (!chain) return;
    used.current = true;
    const s = SETTINGS[mode];
    const t = chain.ctx.currentTime;
    chain.comp.threshold.setTargetAtTime(s.threshold, t, 0.05);
    chain.comp.knee.setTargetAtTime(s.knee, t, 0.05);
    chain.comp.ratio.setTargetAtTime(s.ratio, t, 0.05);
    chain.comp.attack.setTargetAtTime(s.attack, t, 0.05);
    chain.comp.release.setTargetAtTime(s.release, t, 0.05);
    chain.gain.gain.setTargetAtTime(s.gain, t, 0.1);
    void chain.ctx.resume().catch(() => undefined);
  }, [video, mode]);
}
